import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type SyutGridColumn } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import QuickCheckInModal from 'components/modals/modules/booking-module/QuickCheckInModal';
import MarkNoShowConfirmDialog from 'components/modals/modules/booking-module/MarkNoShowConfirmDialog';
import notificationService from 'services/notificationService';
import { propertyService } from 'api/services/propertyService';
import { bookingService } from 'api/services/bookingService';
import { auditCorrectionService } from 'api/services/auditCorrectionService';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import { formatCurrency } from 'utils/currencyFormat';
import type { Booking } from 'api/types/booking.types';
import {
  AUDIT_CORRECTION_LABEL,
  type AuditCorrectionEntry,
  type AuditCorrections,
  type UnresolvedArrival,
} from 'api/types/auditCorrection.types';

/**
 * ============================================================================
 * AUDIT CORRECTIONS — a check-in or no-show missed on a closed date (2026-10-14)
 * ============================================================================
 * Bookings whose stay began before the business date — on a date night audit has
 * closed, or (while the audit catches up) an open date the desk can no longer
 * reach — and that nobody checked in, marked no-show or cancelled. Record the missed check-in (the
 * check-in form, on its real date, with a reason: a stay already over checks
 * out on its booked date, the missed nights post on the next date night audit
 * closes, so the next audit reports them) or mark the
 * no-show. Closed dates never change. Every correction is in the audit log;
 * Recent corrections reads it back (retry Post missed nights when one failed).
 * Own permission module: audit-corrections (view / checkin / mark-no-show).
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

type Section = 'unresolved' | 'recent';
type ArrivalRow = UnresolvedArrival & { id: string };
type EntryRow = AuditCorrectionEntry & { id: string };

const day = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const stamp = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const AuditCorrectionsScreen: React.FC<Props> = ({ tabId, moduleName, allowedActions = [] }) => {
  const canCheckIn = allowedActions.includes('checkin');
  const canNoShow = allowedActions.includes('mark-no-show');

  const { defaultPropertyId } = useDefaultProperty();
  const [propertyId, setPropertyId] = useState('');
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const propertyLabel = usePropertyLabel(propertyId);
  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const [data, setData] = useState<AuditCorrections | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<Section | null>('unresolved');
  const [filterOpen, setFilterOpen] = useState(false);
  const [checkInTarget, setCheckInTarget] = useState<Booking | null>(null);
  const [noShowTarget, setNoShowTarget] = useState<Booking | null>(null);
  const [busyBooking, setBusyBooking] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!propertyId) { setData(null); return; }
    setLoading(true);
    try {
      setData(await auditCorrectionService.get(propertyId));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load audit corrections.');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);
  useEffect(() => { void load(); }, [load]);

  /** The check-in form and the no-show dialog work on the full booking. */
  const openBooking = useCallback(async (bookingId: string, then: (b: Booking) => void) => {
    setBusyBooking(bookingId);
    try {
      const res = await bookingService.getById(bookingId);
      if (!res.data) throw new Error(res.message || 'The booking did not load.');
      then(res.data);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The booking did not load.');
    } finally {
      setBusyBooking(null);
    }
  }, []);

  const postNights = useCallback(async (bookingId: string) => {
    setBusyBooking(bookingId);
    try {
      const result = await auditCorrectionService.postMissedNights(bookingId);
      if (result.failures.length > 0) notificationService.toast.warning(`${result.message} ${result.failures[0]}`);
      else notificationService.toast.success(result.message);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The nights were not posted.');
    } finally {
      setBusyBooking(null);
    }
  }, [load]);

  const arrivals = useMemo<ArrivalRow[]>(() => (data?.unresolved ?? []).map((a) => ({ ...a, id: a.bookingId })), [data]);
  const recent = useMemo<EntryRow[]>(() => (data?.recent ?? []).map((e) => ({ ...e, id: String(e.eventId) })), [data]);
  const failing = recent.filter((r) => r.failures.length > 0).length;

  const arrivalColumns = useMemo<SyutGridColumn<ArrivalRow>[]>(() => [
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 140, cell: ({ row }) => <b>{row.original.bookingNumber}</b> },
    {
      id: 'guestName', header: 'Guest / Party', accessorKey: 'guestName', size: 210,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            {[row.original.partyName, row.original.bookingSource].filter(Boolean).join(' · ') || row.original.guestPhone || ''}
          </div>
        </span>
      ),
    },
    {
      id: 'checkInDate', header: 'Booked stay', accessorKey: 'checkInDate', size: 190,
      cell: ({ row }) => (
        <span>{day(row.original.checkInDate)} → {day(row.original.checkOutDate)}
          {row.original.stayOver && <span className="badge badge-secondary" style={{ fontSize: '0.6rem', marginLeft: '0.35rem' }} title="The booked check-out date has passed">stay over</span>}
          {!row.original.dateClosed && <span className="badge badge-info" style={{ fontSize: '0.6rem', marginLeft: '0.35rem' }} title="Night audit has not closed this date yet — its nights post when the audit closes it">date open</span>}
        </span>
      ),
    },
    { id: 'totalRooms', header: 'Rooms', accessorKey: 'totalRooms', size: 70, cell: ({ row }) => `${row.original.totalRooms} × ${row.original.numNights}n` },
    { id: 'advanceReceived', header: 'Advance', accessorKey: 'advanceReceived', size: 110, cell: ({ row }) => formatCurrency(row.original.advanceReceived) },
    {
      id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 120,
      cell: ({ row }) => <span className={`badge ${row.original.statusCode === 'CONFIRMED' ? 'badge-info' : 'badge-warning'}`} style={{ fontSize: '0.65rem' }}>{row.original.statusName || row.original.statusCode}</span>,
    },
    {
      id: 'actions', header: '', accessorKey: 'bookingId', size: 260,
      cell: ({ row }) => {
        const a = row.original;
        if (a.statusCode !== 'CONFIRMED')
          return <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Confirm or cancel it in Bookings first</span>;
        const busy = busyBooking === a.bookingId;
        return (
          <span style={{ display: 'inline-flex', gap: '0.35rem' }}>
            {canCheckIn && (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                onClick={() => { void openBooking(a.bookingId, setCheckInTarget); }}>
                <IconSystem.check size={13} /> Record check-in
              </button>
            )}
            {canNoShow && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                onClick={() => { void openBooking(a.bookingId, setNoShowTarget); }}>
                <IconSystem.warning size={13} /> No-show
              </button>
            )}
          </span>
        );
      },
    },
  ], [busyBooking, canCheckIn, canNoShow, openBooking]);

  const recentColumns = useMemo<SyutGridColumn<EntryRow>[]>(() => [
    { id: 'at', header: 'When', accessorKey: 'at', size: 130, cell: ({ row }) => stamp(row.original.at) },
    { id: 'userName', header: 'By', accessorKey: 'userName', size: 140, cell: ({ row }) => row.original.userName || '—' },
    { id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 140, cell: ({ row }) => row.original.bookingNumber || '—' },
    {
      id: 'kind', header: 'Correction', accessorKey: 'kind', size: 140,
      cell: ({ row }) => <span className="badge badge-secondary" style={{ fontSize: '0.65rem' }}>{AUDIT_CORRECTION_LABEL[row.original.kind] ?? row.original.kind}</span>,
    },
    {
      id: 'arrivedOn', header: 'Stay', accessorKey: 'arrivedOn', size: 170,
      cell: ({ row }) => row.original.arrivedOn
        ? `${day(row.original.arrivedOn)}${row.original.departedOn ? ` → ${day(row.original.departedOn)}` : ''}` : '—',
    },
    {
      id: 'nightsPosted', header: 'Posted', accessorKey: 'nightsPosted', size: 140,
      cell: ({ row }) => row.original.nightsPosted > 0 ? `${row.original.nightsPosted} line(s) · ${formatCurrency(row.original.amountPosted)}` : '—',
    },
    {
      id: 'reason', header: 'Reason / problems', accessorKey: 'reason', size: 320,
      cell: ({ row }) => (
        <span style={{ whiteSpace: 'normal', fontSize: '0.8rem' }}>
          {row.original.reason || '—'}
          {row.original.failures.map((f) => (
            <div key={f} style={{ color: 'var(--danger, #dc2626)', fontSize: '0.74rem' }}>{f}</div>
          ))}
        </span>
      ),
    },
    {
      id: 'retry', header: '', accessorKey: 'bookingId', size: 150,
      cell: ({ row }) => row.original.failures.length > 0 && canCheckIn && row.original.bookingId ? (
        <button type="button" className="btn btn-secondary btn-sm" disabled={busyBooking === row.original.bookingId}
          title="Post any closed night of this stay still not posted (after fixing the cause)"
          onClick={() => { void postNights(row.original.bookingId); }}>
          <IconSystem.refresh size={13} /> Post missed nights
        </button>
      ) : null,
    },
  ], [busyBooking, canCheckIn, postNights]);

  const section = (id: Section, title: string, count: number, tone: 'warning' | 'danger' | 'default',
    summary: React.ReactNode, body: React.ReactNode) => {
    const isOpen = open === id;
    const color = tone === 'danger' ? 'var(--danger, #dc2626)' : tone === 'warning' ? 'var(--warning-dark, #b45309)' : 'var(--text-primary)';
    return (
      <div key={id} style={{
        display: 'flex', flexDirection: 'column', minHeight: 0,
        flex: isOpen ? '1 1 0' : '0 0 auto', borderBottom: '1px solid var(--border-color)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 0.75rem', background: 'var(--bg-secondary)', flexShrink: 0 }}>
          <button type="button" onClick={() => setOpen(isOpen ? null : id)} aria-expanded={isOpen}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color, fontWeight: 700, fontSize: '0.82rem' }}>
            {isOpen ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
            {title}
            <span className={`badge ${tone === 'danger' ? 'badge-danger' : tone === 'warning' ? 'badge-warning' : 'badge-secondary'}`} style={{ fontSize: '0.65rem' }}>{count}</span>
          </button>
          <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
            {summary}
          </span>
        </div>
        {isOpen && <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{body}</div>}
      </div>
    );
  };

  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    ...(data ? [
      { label: 'Closed through', value: data.closedThrough ? day(data.closedThrough) : 'No date closed yet', icon: 'lock' as const, primary: true },
      { label: 'Nights post on', value: day(data.openDate), icon: 'calendar' as const, title: 'The next date night audit closes: missed nights land in that audit; closed dates never change' },
      { label: 'Night audit from', value: day(data.startDate), icon: 'calendar' as const, title: 'Stays before the start date are reviewed in the migration audit, not here' },
      { label: 'Unresolved', value: String(arrivals.length), icon: arrivals.length ? 'alert' as const : 'checkCircle' as const },
    ] : []),
  ];

  const actionButtons: ActionButton[] = [actionButtonFactory.refresh(() => { void load(); })];

  return (
    <EnterpriseScreenShell
      title="Audit Corrections"
      description="Record a check-in or no-show that was missed on a date night audit has closed. Closed dates never change; every correction is in the audit log."
      icon={<IconSystem.edit size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: propertyId ? 1 : 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  isAsync loadOptions={loadPropertyOptions} defaultOptions cacheOptions={false} isClearable={false}
                  value={propertyId} onChange={(v) => setPropertyId((v as string) || '')}
                  placeholder="Select property..." size="sm" menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody>
        {!propertyId ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property in the filters.</div>
        ) : data && !data.closedThrough ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>
            Night audit has not closed a date at this property yet — use the normal desk actions.
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {section('unresolved', 'Unresolved arrivals', arrivals.length, arrivals.length ? 'warning' : 'default',
              arrivals.length
                ? 'Never checked in, marked no-show or cancelled — record what happened'
                : 'Every past arrival is resolved',
              <SyutGrid data={arrivals} columns={arrivalColumns} isLoading={loading}
                emptyMessage="No unresolved past arrivals." />)}
            {section('recent', 'Recent corrections', recent.length, failing ? 'danger' : 'default',
              failing ? `${failing} with postings that failed — fix the cause, then Post missed nights` : 'From the audit log, newest first',
              <SyutGrid data={recent} columns={recentColumns} isLoading={loading}
                emptyMessage="No corrections recorded yet." />)}
          </div>
        )}
      </ReportBody>

      <QuickCheckInModal
        isOpen={!!checkInTarget}
        booking={checkInTarget}
        auditCorrection
        onClose={() => setCheckInTarget(null)}
        onCheckedIn={() => { void load(); }}
        tabId={tabId}
        moduleName={moduleName}
      />
      <MarkNoShowConfirmDialog
        isOpen={!!noShowTarget}
        booking={noShowTarget}
        auditCorrection
        onClose={() => setNoShowTarget(null)}
        onMarked={() => { void load(); }}
      />
    </EnterpriseScreenShell>
  );
};

export default AuditCorrectionsScreen;
